import React, { useRef } from "react";
import { z } from "zod";

import {
	Alert,
	ButtonStandard,
	type FormHandle,
	FormInputText,
	InputWrapper,
	ModalBase,
	PYForm,
	Text,
} from "@performyard/py-components";

import {
	type EmployeeValue,
	MultipleSelectEmployee,
	SingleSelectEmployee,
} from "../../../../components/Inputs/SelectEmployee";

type WorkspaceFormModalProps = {
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (values: { name: string; participants: EmployeeValue[] }) => void;
	isPending: boolean;
	title: string;
	submitLabel: string;
	defaultName: string;
	defaultParticipants: EmployeeValue[];
	isTeamMeetingsEnabled: boolean;
	participantLimit: number;
	isParticipantEditable: boolean;
	fetchEmployees?: (searchTerm: string) => Promise<{ id: string; name: string }[]>;
};

const nameSchema = z.object({
	name: z
		.string()
		.min(1, { message: "This is a required field" })
		.max(280, { message: "Name cannot exceed 280 characters" }),
});

const WorkspaceFormModal = ({
	isOpen,
	onClose,
	onSubmit,
	isPending,
	title,
	submitLabel,
	defaultName,
	defaultParticipants,
	isTeamMeetingsEnabled,
	participantLimit,
	isParticipantEditable,
	fetchEmployees,
}: WorkspaceFormModalProps) => {
	const formRef = useRef<FormHandle<typeof nameSchema>>(null);
	const [participants, setParticipants] = React.useState<EmployeeValue[]>(defaultParticipants);
	const [participantError, setParticipantError] = React.useState<string>("");

	// Max selectable participants (limit - 1 because creator is auto-included)
	const maxSelectableParticipants = participantLimit - 1;
	const isAtLimit = participants.length >= maxSelectableParticipants;

	// Reset state when modal opens with new data
	React.useEffect(() => {
		if (isOpen) {
			setParticipants(defaultParticipants);
			setParticipantError("");
		}
	}, [isOpen, defaultParticipants]);

	const validateParticipants = () => {
		if (participants.length === 0) {
			setParticipantError("This is a required field");
			return false;
		}
		setParticipantError("");
		return true;
	};

	const handleFormSubmit = ({ value }: { value: { name: string } }) => {
		if (!validateParticipants()) return;
		onSubmit({ name: value.name, participants });
	};

	const handleClose = () => {
		formRef.current?.reset();
		setParticipants(defaultParticipants);
		setParticipantError("");
		onClose();
	};

	const handleMultiSelectChange = (employees: EmployeeValue[]) => {
		setParticipants(employees);
		if (employees.length > 0) {
			setParticipantError("");
		}
	};

	const handleSingleSelectChange = (employeeId: string | null, employeeName: string | null) => {
		if (employeeId && employeeName) {
			setParticipants([{ id: employeeId, name: employeeName }]);
			setParticipantError("");
		} else {
			setParticipants([]);
		}
	};

	return (
		<ModalBase isOpen={isOpen} onClose={handleClose} dangerouslySetClassName="w-[432px] overflow-visible">
			<div className="flex flex-col px-6 pb-6">
				<div className="flex items-center justify-center border-b border-neutral-2 pb-2">
					<Text weight="semi-bold" size="sub-heading">
						{title}
					</Text>
				</div>

				<PYForm
					ref={formRef}
					id="workspace-form"
					schema={nameSchema}
					defaultValues={{ name: defaultName }}
					onSubmit={handleFormSubmit}
					validateOn={["onSubmit"]}
				>
					<div className="space-y-4 pt-2">
						<FormInputText
							id="name"
							label="Meeting Workspace Name"
							placeholder="Meeting Workspace Name Here"
							clearable
							data-testid="workspace-name-input"
						/>

						{isTeamMeetingsEnabled ? (
							<InputWrapper
								id="participants"
								label="Participants"
								labelPosition="top"
								error={participantError || undefined}
								labelRight={
									isParticipantEditable ? (
										<Text size="paragraph-3" color="neutral-8" weight="regular">
											{participants.length}/{maxSelectableParticipants}
										</Text>
									) : undefined
								}
							>
								<MultipleSelectEmployee
									value={participants}
									onChange={handleMultiSelectChange}
									fetchEmployees={fetchEmployees}
									disabled={!isParticipantEditable}
									placeholder="Search for employees..."
									data-testid="workspace-participants-input"
									maxSelections={maxSelectableParticipants}
								/>
								{isAtLimit && isParticipantEditable && (
									<Alert
										type="neutral"
										message="Participant limit reached. Remove someone to add another."
									/>
								)}
							</InputWrapper>
						) : (
							<InputWrapper
								id="participant"
								label="Participant"
								labelPosition="top"
								error={participantError || undefined}
							>
								<SingleSelectEmployee
									value={participants[0] ?? null}
									onChange={handleSingleSelectChange}
									fetchEmployees={fetchEmployees}
									disabled={!isParticipantEditable}
									placeholder="Make a selection"
									data-testid="workspace-participants-input"
								/>
							</InputWrapper>
						)}
					</div>

					<div className="flex justify-center gap-2 border-t border-neutral-2 pt-6">
						<div className="w-fit">
							<ButtonStandard
								size="large"
								variant="outline"
								color="neutral"
								onClick={handleClose}
								disabled={isPending}
							>
								Cancel
							</ButtonStandard>
						</div>
						<div className="w-fit">
							<ButtonStandard
								size="large"
								color="primary"
								type="submit"
								disabled={isPending}
								onClick={validateParticipants}
							>
								{submitLabel}
							</ButtonStandard>
						</div>
					</div>
				</PYForm>
			</div>
		</ModalBase>
	);
};

export default WorkspaceFormModal;
